/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC } from "react"
import { Row, Col, Button } from "@duxton-web/ui"
import { AddImageUniversal } from "@duxton-web/icons"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import classnames from "classnames"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { Item } from "types/models/menu"
import { useMenuEditorStatus } from "pages/menu/detail/hooks/useMenuEditorStatus"
import { ItemAvailabilityDropdownDuxton } from "components/dropdowns"
import { SellingTimeTag } from "components/tags"
import { getSellingTimeByID } from "utils/selling_time_helper"
import { useSellingTimes } from "hooks/useSellingTimes"
import {
  getCategoryByID,
  getSubDepartmentByID,
  isItemWithoutPhotoMode,
} from "utils/menu_helper"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { useTranslationsMap } from "common/utils/i18n"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import {
  isItemUnavailable,
  MenuItemRowCheckbox,
  MenuItemRowColumns,
} from "./shared/MenuItemRowShared"
import styles from "./MenuItemRow.modules.css"

interface IProps {
  item: Item
  showSelection?: boolean
  showDropdown?: boolean
  className?: string
  onClickAddPhoto?: Function
  highlightString?: string
}

export const MenuItemRow: FC<IProps> = ({
  item,
  showSelection,
  showDropdown,
  className,
  onClickAddPhoto = () => {},
  highlightString,
}: IProps) => {
  const dispatch = useDispatch()
  const enablePartialMenu = useEnablePartialMenu()

  const handleCheck = (value: boolean) => {
    dispatch(actionCreator.menu.multiSelectItem(item, value))
  }

  const { sellingTimes } = useSellingTimes()
  const {
    itemSelections,
    isSellingTimeStructure,
    categories,
    subDepartments,
    isMartDepartmentStructure,
    itemFilter,
    enableAlcoholSetting,
    overviewSubDepartments,
  } = useSelector(
    (state: AppState) => ({
      isSellingTimeStructure: state.menu.isSellingTimeStructure,
      itemSelections: state.menu.drawer.itemSelections,
      categories: state.menu.categories,
      subDepartments: state.menu.subDepartments,
      isMartDepartmentStructure: state.menu.isMartDepartmentStructure,
      itemFilter: state.menu.itemFilter,
      enableAlcoholSetting: state.menu.enableAlcoholSetting,
      overviewSubDepartments: state.menu.overview.subDepartments,
    }),
    shallowEqual,
  )

  const { isEditAllowed } = useMenuEditorStatus()

  const category = getCategoryByID(item?.categoryID, categories)

  const subDepartment = getSubDepartmentByID(
    item?.subDepartmentID,
    enablePartialMenu ? overviewSubDepartments : subDepartments,
  )

  const parentSellingTimeID = isMartDepartmentStructure
    ? subDepartment?.sellingTimeID
    : category?.sellingTimeID

  const showSellingTimeTag =
    isSellingTimeStructure && item.sellingTimeID !== parentSellingTimeID

  const selected = itemSelections?.[item?.itemID ?? ""] ?? false

  const spanDropdown = showDropdown ? 8 : 0

  const isUnavailable = isItemUnavailable(item, enableAlcoholSetting)

  /*
    because backend api for selling time is not ready
    uncomment code below to test the changes, remove after backend ready
    don't forget to import { mockSellingSchedules } from "tests/mockMenuState"
  */
  // item.sellingTime = mockSellingSchedules[0]

  const textMap = useTranslationsMap({
    addPhotoButton: "itemPhotoCoverage.button.addPhoto",
  })

  return (
    <Row
      wrap={false}
      gutter={16}
      className={classnames(styles.rowStyle, className)}
    >
      <MenuItemRowColumns
        item={item}
        isUnavailable={isUnavailable}
        highlightString={highlightString}
      />
      {isSellingTimeStructure
        ? showSellingTimeTag && (
            <Col>
              <SellingTimeTag
                sellingTime={getSellingTimeByID(
                  item.sellingTimeID,
                  sellingTimes,
                )}
              />
            </Col>
          )
        : spanDropdown > 0 && (
            <Col span={spanDropdown}>
              <ItemAvailabilityDropdownDuxton item={item} />
            </Col>
          )}

      {isItemWithoutPhotoMode(itemFilter) && isEditAllowed ? (
        <Col
          onClick={event => {
            event.stopPropagation()
            onClickAddPhoto()
            TroyWebTracker.sendEvent({
              category: EventCategory.menuOverviewTab,
              action: EventAction.clickAddPhoto,
              labels: {
                itemID: item.itemID,
                TRAFFIC_MEDIUM: "MISSING_PHOTO",
              },
            })
          }}
        >
          <Button type="secondary" icon={<AddImageUniversal />}>
            {textMap.addPhotoButton}
          </Button>
        </Col>
      ) : (
        <MenuItemRowCheckbox
          showSelection={showSelection}
          selected={selected}
          onCheck={handleCheck}
        />
      )}
    </Row>
  )
}
